/**
 * Type-safe template data for authorization modal
 */

import type { ProjectInfo } from '../../lib/types'
import type { TemplateData } from '../template-engine'

export interface AuthorizationTemplateData extends TemplateData {
  // Display information
  displayName: string
  displayLogo: string
  displayDescription: string

  // User information
  userAvatar: string
  userName: string

  // Domain verification
  origin: string
  domainStatusClass: string
  domainStatusIcon: string
  domainStatusText: string

  // Security warning
  showSecurityWarning: boolean
  expectedDomains: string

  // Button state
  authorizeButtonState: string
}

/**
 * Build template data from project info and metadata
 */
export function buildAuthorizationTemplateData(
  project: ProjectInfo,
  origin: string,
  metadata: { name?: string; logo?: string; description?: string } | null,
  userProfile?: { avatar?: string | null; displayName?: string | null }
): AuthorizationTemplateData {
  const isVerifiedOrigin = project.domains.includes(origin)

  // Use metadata if available, fallback to project info
  const displayName = metadata?.name || project.name
  const displayLogo = metadata?.logo || project.logoUrl || ''
  const displayDescription = metadata?.description || ''

  // User avatar/logo for right side
  const userAvatar = userProfile?.avatar || ''
  const userName = userProfile?.displayName || 'embarkAI Wallet'

  return {
    // Display
    displayName,
    displayLogo,
    displayDescription,

    // User
    userAvatar,
    userName,

    // Domain
    origin,
    domainStatusClass: isVerifiedOrigin ? 'verified' : 'unverified',
    domainStatusIcon: isVerifiedOrigin ? '✓' : '⚠️',
    domainStatusText: isVerifiedOrigin ? 'Verified domain:' : 'Unverified domain:',

    // Security
    showSecurityWarning: !isVerifiedOrigin,
    expectedDomains: project.domains.join(', '),

    // Button
    authorizeButtonState: !isVerifiedOrigin ? 'disabled' : ''
  }
}
